/**
 * Expert-level template service for frontend.
 * Handles template synchronization with backend microservice.
 */

import axios, { AxiosResponse } from 'axios';

// Configuration
// Utiliser la variable d'environnement VITE_API_URL avec fallback pour localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';
console.log('🔧 API_BASE_URL utilisé:', API_BASE_URL);
const API_TIMEOUT = 30000; // 30 seconds

// API Client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter Idempotency-Key aux requêtes POST (uniquement si pas en localhost)
apiClient.interceptors.request.use((config) => {
  if ((config.method === 'post' || config.method === 'POST') &&
    !API_BASE_URL.includes('localhost') &&
    !API_BASE_URL.includes('127.0.0.1')) {
    // Générer un Idempotency-Key unique pour chaque requête POST (uniquement en production/dev distant)
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    config.headers['Idempotency-Key'] = idempotencyKey;
  }
  return config;
});

// Types
export interface TemplateData {
  template_id: string;
  component_name: string;
  file_path: string;
  category?: string;
  industry?: string;
  style?: string;
  name?: string;
  description?: string;
  tags?: string[];
  is_active?: boolean;
  downloads?: number;
  rating?: number;
  createdAt?: string;
}

export interface SyncResponse {
  status: string;
  message: string;
  synced_count: number;
  total_templates?: number;
  templates: Array<{
    template_id: string;
    name: string;
    status: string;
  }>;
  errors?: string[];
}

export interface Template {
  id: string;
  template_id: string;
  name: string;
  category: string;
  industry: string;
  style: string;
  has_photo: boolean;
  preview_url?: string;
  thumbnail_url?: string;
  downloads_count: number;
  rating: number;
  is_active: boolean;
}

export interface TemplateListResponse {
  templates: Template[];
  pagination: {
    page: number;
    page_size: number;
    total_pages: number;
    total_count: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/**
 * Template Service Class
 */
export class TemplateService {
  /**
   * Synchronize templates with backend microservice
   */
  static async syncTemplates(templates: TemplateData[]): Promise<SyncResponse> {
    try {
      console.log(`Syncing ${templates.length} templates with backend...`);

      const response: AxiosResponse<SyncResponse> = await apiClient.post(
        '/templates/sync/',
        { templates }
      );

      console.log(`Sync completed: ${response.data.synced_count} templates`);

      // Check if there are errors (only if the field exists)
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Sync completed with errors:', response.data.errors);
      }

      return response.data;

    } catch (error) {
      console.error('Template sync failed:', error);
      throw new Error(`Template synchronization failed: ${error}`);
    }
  }

  /**
   * Get templates list with filters
   */
  static async getTemplates(params: {
    category?: string;
    industry?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<TemplateListResponse> {
    try {
      const response: AxiosResponse<TemplateListResponse> = await apiClient.get(
        '/templates/',
        { params }
      );

      return response.data;

    } catch (error) {
      console.error('Failed to fetch templates:', error);
      throw new Error(`Failed to fetch templates: ${error}`);
    }
  }

  /**
   * Get template details
   */
  static async getTemplate(templateId: string): Promise<Template> {
    try {
      const response: AxiosResponse<Template> = await apiClient.get(
        `/templates/${templateId}/`
      );

      return response.data;

    } catch (error) {
      console.error(`Failed to fetch template ${templateId}:`, error);
      throw new Error(`Failed to fetch template: ${error}`);
    }
  }

  /**
   * Get template statistics
   */
  static async getTemplateStats(): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.get('/templates/stats/');
      return response.data;

    } catch (error) {
      console.error('Failed to fetch template stats:', error);
      throw new Error(`Failed to fetch template stats: ${error}`);
    }
  }
}

/**
 * Template Discovery Service
 * Automatically discovers templates in the frontend
 */
export class TemplateDiscoveryService {
  /**
   * Discover all available templates in the frontend
   */
  static discoverTemplates(): TemplateData[] {
    const templates: TemplateData[] = [];

    // Modern Templates
    templates.push({
      template_id: 'modern_tech_001',
      component_name: 'ModernTech002',
      file_path: '/templates/modern/ModernTech002.jsx',
      category: 'modern',
      industry: 'tech',
      style: 'professional',
      name: 'Modern Tech Professional',
      description: 'Clean and modern template for tech professionals',
      tags: ['modern', 'tech', 'professional', 'clean'],
      is_active: true
    });

    templates.push({
      template_id: 'modern_business_001',
      component_name: 'ModernBusiness001',
      file_path: '/templates/modern/ModernBusiness001.jsx',
      category: 'modern',
      industry: 'business',
      style: 'professional',
      name: 'Modern Business Professional',
      description: 'Elegant template for business professionals',
      tags: ['modern', 'business', 'professional', 'elegant'],
      is_active: true
    });

    templates.push({
      template_id: 'modern_modernbusiness002',
      component_name: 'ModernBusiness002',
      file_path: '/templates/modern/ModernBusiness002.jsx',
      category: 'modern',
      industry: 'business',
      style: 'professional',
      name: 'Modern Business Professional 002',
      description: 'Elegant template with beige background for business professionals',
      tags: ['modern', 'business', 'professional', 'elegant', 'beige'],
      is_active: true
    });

    templates.push({
      template_id: 'modern_modernbusiness003',
      component_name: 'ModernBusiness003',
      file_path: '/templates/modern/ModernBusiness003.jsx',
      category: 'modern',
      industry: 'business',
      style: 'professional',
      name: 'Modern Business Professional 003',
      description: 'Professional template with vertical timeline for work experience',
      tags: ['modern', 'business', 'professional', 'timeline', 'skyblue'],
      is_active: true
    });

    templates.push({
      template_id: 'modern_modernbusiness004',
      component_name: 'ModernBusiness004',
      file_path: '/templates/modern/ModernBusiness004.jsx',
      category: 'modern',
      industry: 'business',
      style: 'professional',
      name: 'Modern Business Professional 004',
      description: 'Elegant template with centered name and two columns layout',
      tags: ['modern', 'business', 'professional', 'elegant', 'centered'],
      is_active: true
    });

    templates.push({
      template_id: 'modern_modernbusiness005',
      component_name: 'ModernBusiness005',
      file_path: '/templates/modern/ModernBusiness005.jsx',
      category: 'modern',
      industry: 'business',
      style: 'professional',
      name: 'Modern Business Professional 005',
      description: 'Elegant template with sky blue header and dark sidebar layout',
      tags: ['modern', 'business', 'professional', 'elegant', 'sidebar', 'skyblue'],
      is_active: true
    });

    // Add more templates as they are created
    // templates.push({...});

    return templates;
  }

  /**
   * Auto-sync templates with backend
   */
  static async autoSyncTemplates(): Promise<SyncResponse> {
    try {
      const discoveredTemplates = this.discoverTemplates();
      return await TemplateService.syncTemplates(discoveredTemplates);

    } catch (error) {
      console.error('Auto-sync failed:', error);
      throw error;
    }
  }
}

export default TemplateService;
